import { QuantityInput } from '@cibo/ui/src/components/Form/inputs/QuantityInput/QuantityInput'
import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

const TARGET_DATE_YEAR_SCOPE = 76
const currentYear = new Date().getFullYear()
const TARGET_YEARS = new Array(TARGET_DATE_YEAR_SCOPE).fill(0).map((v, i) => currentYear + 1 + i)

// This is a workaround due to mui-formik and mui broadly not supporting shrink in compatible ways
const NotchedTargetYearSelect = styled(Select)(() => ({
  '.MuiOutlinedInput-notchedOutline legend': { maxWidth: '100%', visibility: 'hidden' },
  '.MuiOutlinedInput-notchedOutline legend:before': {
    content: '"Target year"',
  },
}))

export interface TargetObject {
  name: string
  baseline: string
  value?: number
  default: number
  max: number
  min: number
  increment: number
  setValue: (val: number) => void
}

interface TargetEditorProps {
  inputAdornment: { start: string; end?: string }
  hasTarget?: boolean
  title: string
  createTarget?: () => void
  removeTarget?: () => void
  optional?: boolean
  targets?: TargetObject[]
  year: { value?: number | ''; setValue: (val: number) => void }
}

export const TargetEditor = ({
  inputAdornment,
  title,
  targets,
  year,
  optional,
  hasTarget,
  createTarget,
  removeTarget,
}: TargetEditorProps) => {
  const { t } = useTranslation('impact/InventoryTargets')
  const { palette } = useTheme()
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
      <Stack padding={3} spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack spacing={1}>
            <Typography variant="h6">{title}</Typography>
            {optional && <Typography variant="caption">{t('optional')}</Typography>}
          </Stack>
          {hasTarget && removeTarget && (
            <Button color="secondary" onClick={() => removeTarget()}>
              {t('removeTarget')}
            </Button>
          )}
          {!hasTarget && createTarget && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              color="secondary"
              onClick={createTarget}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('setTargets')}
            </Button>
          )}
        </Stack>
      </Stack>
      <Collapse in={!!hasTarget}>
        <Stack spacing={3} padding={3} sx={{ pt: 0 }}>
          {targets?.map(target => {
            const currentValue = target.value === undefined ? target.default : target.value
            return (
              <Paper key={target.name} variant="outlined" sx={{ borderRadius: '12px' }}>
                <Stack direction="row" justifyContent="space-between" p={1.5}>
                  <Stack direction="column">
                    <Typography>{target.name}</Typography>
                    <Typography color="text.secondary">{target.baseline}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body2">{inputAdornment.start}</Typography>
                    <Paper
                      variant="outlined"
                      style={{
                        borderRadius: '4px',
                        backgroundColor: palette.background.default,
                      }}
                    >
                      {!!hasTarget && (
                        <QuantityInput
                          onChange={target.setValue}
                          value={currentValue}
                          minValue={target.min}
                          maxValue={target.max}
                          endAdornment={inputAdornment.end}
                          increment={target.increment}
                        />
                      )}
                    </Paper>
                  </Stack>
                </Stack>
              </Paper>
            )
          })}
          <FormControl style={{ alignSelf: 'flex-end', width: '220px' }}>
            <InputLabel shrink htmlFor="absoluteEmissionsYear">
              {t('targetYear')}
            </InputLabel>
            <NotchedTargetYearSelect
              required
              value={year.value || ' '}
              size="medium"
              id="absoluteEmissionsYear"
              onChange={(event: SelectChangeEvent<any>) => year.setValue(+event.target.value)}
            >
              {TARGET_YEARS.map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </NotchedTargetYearSelect>
          </FormControl>
        </Stack>
      </Collapse>
    </Paper>
  )
}
